import { CSElementPlugin } from '../types/plugin-interfaces';
import { CSElement } from '../core/CSElement';

export enum CacheStrategy {
    LRU = 'lru',
    LFU = 'lfu',
    TTL = 'ttl'
}

export interface CacheEntry {
    value: any;
    timestamp: number;
    accessCount: number;
    ttl?: number;
}

export class CachePlugin implements CSElementPlugin {
    public readonly name = 'CachePlugin';
    public readonly version = '1.0.0';
    public readonly description = 'Плагин для кэширования данных';

    private cache = new Map<string, CacheEntry>();
    private maxSize: number = 1000;
    private strategy: CacheStrategy = CacheStrategy.LRU;
    private stats = {
        hits: 0,
        misses: 0
    };

    constructor(options?: { maxSize?: number; strategy?: CacheStrategy }) {
        if (options) {
            this.maxSize = options.maxSize || this.maxSize;
            this.strategy = options.strategy || this.strategy;
        }
    }

    install(CSElementClass: typeof CSElement): void {
        console.log(`CachePlugin установлен для ${CSElementClass.name}`);
    }

    set(key: string, value: any, ttl?: number): void {
        if (this.cache.size >= this.maxSize) {
            this.evict();
        }

        this.cache.set(key, {
            value,
            timestamp: Date.now(),
            accessCount: 0,
            ttl
        });
    }

    get(key: string): any {
        const entry = this.cache.get(key);
        if (!entry) {
            this.stats.misses++;
            return undefined;
        }

        // Проверяем TTL
        if (entry.ttl && Date.now() - entry.timestamp > entry.ttl) {
            this.cache.delete(key);
            this.stats.misses++;
            return undefined;
        }

        // Обновляем статистику доступа
        entry.accessCount++;
        entry.timestamp = Date.now();
        this.stats.hits++;

        return entry.value;
    }

    has(key: string): boolean {
        const entry = this.cache.get(key);
        if (!entry) {
            return false;
        }

        // Проверяем TTL
        if (entry.ttl && Date.now() - entry.timestamp > entry.ttl) {
            this.cache.delete(key);
            return false;
        }

        return true;
    }

    delete(key: string): boolean {
        return this.cache.delete(key);
    }

    clear(): void {
        this.cache.clear();
    }

    size(): number {
        return this.cache.size;
    }

    keys(): string[] {
        return Array.from(this.cache.keys());
    }

    cacheQueryResult(query: string, elementId: string, result: any): void {
        const key = `query:${query}:${elementId}`;
        this.set(key, result);
    }

    getStats(): { size: number; maxSize: number; strategy: string; hitRate?: number; hits?: number; misses?: number; entries?: number } {
        const totalRequests = this.stats.hits + this.stats.misses;
        const hitRate = totalRequests > 0 ? (this.stats.hits / totalRequests) * 100 : 0;
        
        return {
            size: this.cache.size,
            maxSize: this.maxSize,
            strategy: this.strategy,
            hits: this.stats.hits,
            misses: this.stats.misses,
            entries: this.cache.size,
            hitRate: Math.round(hitRate)
        };
    }

    getCachedQueryResult(query: string, elementId: string): any {
        const key = `query:${query}:${elementId}`;
        return this.get(key);
    }

    cacheSerializationResult(elementId: string, options: any, result: any): void {
        const key = `serialization:${elementId}:${JSON.stringify(options)}`;
        this.set(key, result);
    }

    getCachedSerializationResult(elementId: string, options: any): any {
        const key = `serialization:${elementId}:${JSON.stringify(options)}`;
        return this.get(key);
    }

    resetStats(): void {
        this.stats.hits = 0;
        this.stats.misses = 0;
    }

    updateConfig(options: { maxSize?: number; strategy?: CacheStrategy }): void {
        if (options.maxSize !== undefined) {
            this.maxSize = options.maxSize;
        }
        if (options.strategy !== undefined) {
            this.strategy = options.strategy;
        }
    }

    getConfig(): { maxSize: number; strategy: CacheStrategy } {
        return {
            maxSize: this.maxSize,
            strategy: this.strategy
        };
    }

    private evict(): void {
        if (this.cache.size === 0) return;

        let keyToEvict: string | undefined;

        switch (this.strategy) {
            case CacheStrategy.LRU:
                keyToEvict = this.findLRUKey();
                break;
            case CacheStrategy.LFU:
                keyToEvict = this.findLFUKey();
                break;
            case CacheStrategy.TTL:
                keyToEvict = this.findExpiredKey() || this.findLRUKey();
                break;
            default:
                keyToEvict = this.cache.keys().next().value;
        }

        if (keyToEvict) {
            this.cache.delete(keyToEvict);
        }
    }

    private findLRUKey(): string | undefined {
        let oldestKey: string | undefined;
        let oldestTime = Date.now();

        for (const [key, entry] of this.cache) {
            if (entry.timestamp < oldestTime) {
                oldestTime = entry.timestamp;
                oldestKey = key;
            }
        }

        return oldestKey;
    }

    private findLFUKey(): string | undefined {
        let leastUsedKey: string | undefined;
        let leastCount = Infinity;

        for (const [key, entry] of this.cache) {
            if (entry.accessCount < leastCount) {
                leastCount = entry.accessCount;
                leastUsedKey = key;
            }
        }

        return leastUsedKey;
    }

    private findExpiredKey(): string | null {
        const now = Date.now();
        for (const [key, entry] of this.cache) {
            if (entry.ttl && now - entry.timestamp > entry.ttl) {
                return key;
            }
        }
        return null;
    }
}

export function cachePlugin(options?: { maxSize?: number; strategy?: CacheStrategy }): CachePlugin {
    return new CachePlugin(options);
} 